import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import { Crafting__factory } from 'public/abi/evm/types';
import type { CraftAvailableForClaim, UnclaimedRecipeProps } from '~/types/crafting';

export default () => {
  const { address } = useWeb3ModalAccount();
  const getUserActivitiesShort = async (
    recipes: UnclaimedRecipeProps[],
    signal?: AbortSignal
  ): Promise<CraftAvailableForClaim[]> => {
    const { blockchain } = useEnvs();
    const { getProvider } = useProvider();

    const craftingContract = Crafting__factory.connect(blockchain.contracts.crafting, getProvider());

    if (signal?.aborted) {
      throw new Error('Request was cancelled');
    }
    const recipesStatuses = await Promise.all(
      recipes.map(async (recipe) => {
        const data = await craftingContract?.getUserRecipeStatuses(address.value || '', recipe.recipeId, 0, 1000);
        return {
          recipe,
          data: data.map((item) => [
            item[0].toString(),
            item[1].toString(),
            item[2].toString(),
            item[3].toString(),
            item[4].toString(),
            item[5].toString()
          ])
        };
      })
    );

    return recipesStatuses.flatMap(({ recipe, data }) => {
      const filteredData = data.filter((item: any) => Number(item[3]) > 0);
      if (filteredData.length === 0) return [];

      return [
        {
          recipe,
          available: filteredData.reduce((sum: number, item: any) => sum + Number(item[3]), 0),
          id: filteredData.map((item: any) => BigNumber(item[5]).toNumber()),
          unclaimedByRecipeIndexes: Object.fromEntries(
            data
              .filter((item: any) => Number(item[3]) > 0)
              .map((item: any) => [
                BigNumber(item[5]).toNumber(),
                {
                  unclaimedModifier: BigNumber(item[3]).toNumber(),
                  multiplyModifier: BigNumber(item[1]).toNumber(),
                  buildingHash: String(item[4]),
                  recipeId: recipe.recipeId.toString(),
                  userRecipeIndex: BigNumber(item[5]).toNumber(),
                  recipe
                }
              ])
          )
        }
      ];
    });
  };

  const splitActivitiesToChunks = (recipeIds: number[], indexes: Record<number, number>[]): [number[], number[][]] => {
    const recipeIdsFormatted: number[] = [];
    const indexesFormatted: number[][] = [];

    indexes.forEach((obj, index) => {
      const values = Object.values(obj);
      let chunk: number[] = [];

      values.forEach((value, i) => {
        chunk.push(value);

        if (chunk.length === 10 || i === values.length - 1) {
          indexesFormatted.push(chunk);
          recipeIdsFormatted.push(recipeIds[index]);
          chunk = [];
        }
      });
    });

    return [recipeIdsFormatted, indexesFormatted];
  };

  return {
    getUserActivitiesShort,
    splitActivitiesToChunks
  };
};
